import { WidgetApi } from './api.js';
import type { BookAppointmentResponse, BookWindowResponse, DoctorDto, PublicConfig, SlotDto, WindowDto } from './types.js';
import { el, toLocalDateInputValue, dateInTimeZone, hourInTimeZone, timeInTimeZone, stripTimeZoneAbbrev } from './dom-utils.js';

// Crisp SVG instead of the ✅ emoji glyph (renders differently per OS/
// browser/font — see the identical reasoning on the chat launcher icon in
// widget.ts) for the booking-confirmed screen.
const SUCCESS_CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

export interface BookingFormOptions {
  api: WidgetApi;
  branding: PublicConfig | null;
  /** Container the form is rendered into — the modal's body, or a plain
   * page container for an inline embed (see widget.ts's data-practigo-book
   * scan). Fully owns its children; nothing else should write into it. */
  mount: HTMLElement;
  /** Present for the modal (closes it); absent for an inline embed, where
   * the post-success screen instead offers "Book another appointment" and
   * resets the form in place rather than closing anything. */
  onDone?: () => void;
}

/**
 * The one real booking flow — doctor -> date -> exact slot or working
 * window (TOKEN_QUEUE doctors, see DoctorDto.usesTokenQueue) -> contact
 * details -> optional OTP verification -> submit. Used identically by the
 * floating chat widget's "Book an appointment" modal and by an inline
 * `data-practigo-book` embed on a clinic-site page — one implementation, one
 * set of tested booking/OTP/window-vs-slot logic, two mount points.
 */
export class BookingForm {
  private api: WidgetApi;
  private branding: PublicConfig | null;
  private mount: HTMLElement;
  private onDone?: () => void;

  private doctorSelect!: HTMLSelectElement;
  private dateInput!: HTMLInputElement;
  private slotsList!: HTMLDivElement;
  private nameInput!: HTMLInputElement;
  private phoneInput!: HTMLInputElement;
  private emailInput!: HTMLInputElement;
  private reasonInput!: HTMLInputElement;
  private submitBtn!: HTMLButtonElement;
  private errorEl!: HTMLDivElement;
  private formNodes: HTMLElement[] = [];

  private doctors: DoctorDto[] = [];
  private currentSlots: SlotDto[] = [];
  private selectedSlot: SlotDto | null = null;
  private currentWindows: WindowDto[] = [];
  private selectedWindow: WindowDto | null = null;

  private otpInput!: HTMLInputElement;
  private otpVerifyBtn!: HTMLButtonElement;
  private patientAccessToken: string | null = null;
  private verifiedPhone: string | null = null;
  private verifiedEmail: string | null = null;

  constructor(opts: BookingFormOptions) {
    this.api = opts.api;
    this.branding = opts.branding;
    this.mount = opts.mount;
    this.onDone = opts.onDone;
  }

  /** Builds the form fields into `mount` and kicks off the doctor list load. */
  async render(): Promise<void> {
    const doctorField = el('div', 'pg-field');
    const doctorLabel = el('label');
    doctorLabel.textContent = 'Provider';
    this.doctorSelect = el('select');
    this.doctorSelect.addEventListener('change', () => this.refreshAvailability());
    doctorField.appendChild(doctorLabel);
    doctorField.appendChild(this.doctorSelect);

    const dateField = el('div', 'pg-field');
    const dateLabel = el('label');
    dateLabel.textContent = 'Date';
    this.dateInput = el('input');
    this.dateInput.type = 'date';
    const today = new Date();
    this.dateInput.value = toLocalDateInputValue(today);
    this.dateInput.min = toLocalDateInputValue(today);
    this.dateInput.addEventListener('change', () => this.refreshAvailability());
    dateField.appendChild(dateLabel);
    dateField.appendChild(this.dateInput);

    const slotsField = el('div', 'pg-field');
    const slotsLabel = el('label');
    slotsLabel.textContent = 'Available times';
    this.slotsList = el('div', 'pg-slots-list');
    slotsField.appendChild(slotsLabel);
    slotsField.appendChild(this.slotsList);

    const nameField = el('div', 'pg-field');
    const nameLabel = el('label');
    nameLabel.textContent = 'Full name';
    this.nameInput = el('input');
    this.nameInput.type = 'text';
    this.nameInput.placeholder = 'Enter your full name';
    this.nameInput.addEventListener('input', () => this.updateSubmitState());
    nameField.appendChild(nameLabel);
    nameField.appendChild(this.nameInput);

    // Neither field is unconditionally required — createPatientSchema's
    // phone-or-email rule applies regardless of whether this clinic has OTP
    // verification turned on (see routes.ts's identical server-side gating).
    // When a WhatsApp number IS given, it still gets OTP-verified before
    // booking (handleSubmit below) whenever that's turned on.
    const phoneField = el('div', 'pg-field');
    const phoneLabel = el('label');
    phoneLabel.textContent = 'WhatsApp number (or email below)';
    this.phoneInput = el('input');
    this.phoneInput.type = 'tel';
    this.phoneInput.placeholder = 'Enter your WhatsApp number';
    this.phoneInput.addEventListener('input', () => this.updateSubmitState());
    phoneField.appendChild(phoneLabel);
    phoneField.appendChild(this.phoneInput);

    const emailField = el('div', 'pg-field');
    const emailLabel = el('label');
    emailLabel.textContent = 'Email (or WhatsApp number above)';
    this.emailInput = el('input');
    this.emailInput.type = 'email';
    this.emailInput.placeholder = 'Enter your email';
    this.emailInput.addEventListener('input', () => this.updateSubmitState());
    emailField.appendChild(emailLabel);
    emailField.appendChild(this.emailInput);

    const reasonField = el('div', 'pg-field');
    const reasonLabel = el('label');
    reasonLabel.textContent = 'Reason for visit (optional)';
    this.reasonInput = el('input');
    this.reasonInput.type = 'text';
    this.reasonInput.placeholder = 'Routine cleaning, tooth pain, ...';
    reasonField.appendChild(reasonLabel);
    reasonField.appendChild(this.reasonInput);

    this.errorEl = el('div', 'pg-form-error');
    this.errorEl.style.display = 'none';

    this.submitBtn = el('button', 'pg-submit-btn');
    this.submitBtn.type = 'button';
    this.submitBtn.textContent = 'Confirm booking';
    this.submitBtn.disabled = true;
    this.submitBtn.style.background = this.branding?.primaryColor ?? '#2563eb';
    this.submitBtn.addEventListener('click', () => this.handleSubmit());

    this.formNodes = [doctorField, dateField, slotsField, nameField, phoneField, emailField, reasonField, this.errorEl, this.submitBtn];
    this.showForm();
    await this.loadDoctors();
  }

  private showForm() {
    this.mount.replaceChildren(...this.formNodes);
    this.errorEl.style.display = 'none';
  }

  private async loadDoctors() {
    this.doctorSelect.innerHTML = '';
    const loadingOpt = el('option');
    loadingOpt.textContent = 'Loading providers…';
    this.doctorSelect.appendChild(loadingOpt);
    try {
      this.doctors = await this.api.getDoctors();
      this.doctorSelect.innerHTML = '';
      if (this.doctors.length === 0) {
        const opt = el('option');
        opt.textContent = 'No providers available';
        this.doctorSelect.appendChild(opt);
        return;
      }
      for (const doctor of this.doctors) {
        const opt = el('option');
        opt.value = doctor.id;
        opt.textContent = `${doctor.name} — ${doctor.specialty}`;
        this.doctorSelect.appendChild(opt);
      }
      await this.refreshAvailability();
    } catch (err) {
      this.showError(err instanceof Error ? err.message : 'Unable to load providers right now.');
    }
  }

  /** Dispatches to the exact-slot grid or the window picker depending on the
   * selected doctor — see DoctorDto.usesTokenQueue. */
  private async refreshAvailability() {
    const doctorId = this.doctorSelect.value;
    const doctor = this.doctors.find((d) => d.id === doctorId);
    if (doctor?.usesTokenQueue) {
      await this.refreshWindows();
    } else {
      await this.refreshSlots();
    }
  }

  private async refreshSlots() {
    this.selectedSlot = null;
    this.selectedWindow = null;
    this.updateSubmitState();
    const doctorId = this.doctorSelect.value;
    if (!doctorId || !this.dateInput.value) return;

    this.slotsList.innerHTML = '';
    const loading = el('div', 'pg-slot-empty');
    loading.textContent = 'Loading times…';
    this.slotsList.appendChild(loading);

    const requestedDate = this.dateInput.value;
    const dayStart = new Date(`${requestedDate}T00:00:00`);
    const from = new Date(dayStart.getTime() - 24 * 60 * 60 * 1000);
    const to = new Date(dayStart.getTime() + 2 * 24 * 60 * 60 * 1000);
    try {
      const slots = await this.api.getAvailability(doctorId, from.toISOString(), to.toISOString());
      this.currentSlots = slots.filter((slot) => dateInTimeZone(slot.startTimeIso, slot.timezone) === requestedDate);
      this.renderSlots();
    } catch (err) {
      this.slotsList.innerHTML = '';
      this.showError(err instanceof Error ? err.message : 'Unable to load availability.');
    }
  }

  /** Morning/Afternoon/Evening bucket for an exact slot — see renderSlots. */
  private static periodForHour(hour: number): 'Morning' | 'Afternoon' | 'Evening' {
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
  }

  private renderSlots() {
    this.slotsList.innerHTML = '';
    if (this.currentSlots.length === 0) {
      const empty = el('div', 'pg-slot-empty');
      empty.textContent = 'No open times that day. Try another date.';
      this.slotsList.appendChild(empty);
      return;
    }

    // A doctor without token-queue booking can still have a full day of
    // 15/30-minute slots — grouping them under Morning/Afternoon/Evening
    // headers keeps that from reading as one long undifferentiated list,
    // closer to the "at a glance" feel token-queue doctors get for free from
    // their coarser windows (see renderWindows). Booking still targets an
    // exact slot underneath; this is a display grouping only.
    const groups = new Map<'Morning' | 'Afternoon' | 'Evening', SlotDto[]>();
    for (const slot of this.currentSlots) {
      const period = BookingForm.periodForHour(hourInTimeZone(slot.startTimeIso, slot.timezone));
      const bucket = groups.get(period);
      if (bucket) bucket.push(slot);
      else groups.set(period, [slot]);
    }

    for (const period of ['Morning', 'Afternoon', 'Evening'] as const) {
      const slots = groups.get(period);
      if (!slots || slots.length === 0) continue;

      const first = timeInTimeZone(slots[0]!.startTimeIso, slots[0]!.timezone);
      const last = timeInTimeZone(slots[slots.length - 1]!.startTimeIso, slots[slots.length - 1]!.timezone);
      const header = el('div', 'pg-slot-group-header');
      header.textContent = `${period} (${first} – ${last})`;
      this.slotsList.appendChild(header);

      const row = el('div', 'pg-slot-group');
      for (const slot of slots) {
        const btn = el('button', 'pg-slot-btn');
        btn.type = 'button';
        btn.textContent = timeInTimeZone(slot.startTimeIso, slot.timezone);
        btn.addEventListener('click', () => {
          this.selectedSlot = slot;
          this.slotsList.querySelectorAll('.pg-slot-btn').forEach((node) => {
            node.classList.remove('selected');
            (node as HTMLElement).style.background = '';
          });
          btn.classList.add('selected');
          btn.style.background = this.branding?.primaryColor ?? '#2563eb';
          this.updateSubmitState();
        });
        row.appendChild(btn);
      }
      this.slotsList.appendChild(row);
    }
  }

  /** Window-picker equivalent of refreshSlots — GET /api/widget/windows,
   * queried for the selected calendar date directly. */
  private async refreshWindows() {
    this.selectedSlot = null;
    this.selectedWindow = null;
    this.updateSubmitState();
    const doctorId = this.doctorSelect.value;
    if (!doctorId || !this.dateInput.value) return;

    this.slotsList.innerHTML = '';
    const loading = el('div', 'pg-slot-empty');
    loading.textContent = 'Loading windows…';
    this.slotsList.appendChild(loading);

    try {
      this.currentWindows = await this.api.getWindows(doctorId, this.dateInput.value);
      this.renderWindows();
    } catch (err) {
      this.slotsList.innerHTML = '';
      this.showError(err instanceof Error ? err.message : 'Unable to load availability.');
    }
  }

  private renderWindows() {
    this.slotsList.innerHTML = '';
    if (this.currentWindows.length === 0) {
      const empty = el('div', 'pg-slot-empty');
      empty.textContent = 'No working hours that day. Try another date.';
      this.slotsList.appendChild(empty);
      return;
    }
    for (const window of this.currentWindows) {
      const btn = el('button', 'pg-slot-btn');
      btn.type = 'button';
      btn.textContent = `${timeInTimeZone(window.startTimeIso, window.timezone)} – ${timeInTimeZone(window.endTimeIso, window.timezone)}`;
      btn.addEventListener('click', () => {
        this.selectedWindow = window;
        this.slotsList.querySelectorAll('.pg-slot-btn').forEach((node) => {
          node.classList.remove('selected');
          (node as HTMLElement).style.background = '';
        });
        btn.classList.add('selected');
        btn.style.background = this.branding?.primaryColor ?? '#2563eb';
        this.updateSubmitState();
      });
      this.slotsList.appendChild(btn);
    }
  }

  private updateSubmitState() {
    // Either a WhatsApp number or an email is enough — never both required —
    // matching createPatientSchema's phone-or-email rule the backend
    // enforces (apps/api/src/modules/widget/routes.ts).
    const hasPhone = this.phoneInput.value.trim().length >= 5;
    const hasEmail = /\S+@\S+\.\S+/.test(this.emailInput.value.trim());
    const hasContact = hasPhone || hasEmail;
    const ready = (!!this.selectedSlot || !!this.selectedWindow) && this.nameInput.value.trim().length > 0 && hasContact;
    this.submitBtn.disabled = !ready;
  }

  private showError(message: string) {
    this.errorEl.textContent = message;
    this.errorEl.style.display = 'block';
  }

  /**
   * Entry point for the "Confirm booking" button. Whichever contact method
   * was actually given gets OTP-verified first (unless this clinic has
   * verification turned off): a WhatsApp number via WhatsApp/SMS, or — when
   * no WhatsApp number was entered at all — the email address instead. There
   * is always something to verify; verification is never simply skipped.
   */
  private async handleSubmit() {
    if (!this.selectedSlot && !this.selectedWindow) return;
    this.errorEl.style.display = 'none';

    if (this.branding && !this.branding.requiresPhoneVerificationForBooking) {
      await this.performBooking();
      return;
    }

    const phone = this.phoneInput.value.trim();
    if (phone) {
      if (this.patientAccessToken && this.verifiedPhone === phone) {
        await this.performBooking();
        return;
      }
      await this.beginVerification('phone', phone);
      return;
    }

    const email = this.emailInput.value.trim();
    if (email) {
      if (this.patientAccessToken && this.verifiedEmail === email) {
        await this.performBooking();
        return;
      }
      await this.beginVerification('email', email);
      return;
    }

    // Neither given — the submit button is disabled in this state
    // (updateSubmitState), but book directly as a last resort rather than
    // getting stuck with no verification target.
    await this.performBooking();
  }

  private async beginVerification(kind: 'phone' | 'email', value: string) {
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Sending code…';
    try {
      if (kind === 'phone') await this.api.requestOtp(value);
      else await this.api.requestEmailOtp(value);
    } catch (err) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Confirm booking';
      this.showError(err instanceof Error ? err.message : 'Unable to send a verification code right now.');
      return;
    }
    this.renderOtpStep(kind, value);
  }

  private renderOtpStep(kind: 'phone' | 'email', value: string) {
    const field = el('div', 'pg-field');
    const label = el('label');
    label.textContent = `Enter the 6-digit code we sent to ${value}`;
    field.appendChild(label);

    // The real value lives on this hidden input — every existing listener
    // (enabling the verify button, submitting on Enter) attaches here
    // unchanged; the 6 visible boxes below just keep it in sync. Segmented
    // digit boxes (auto-advance, backspace-to-previous, paste-the-whole-code)
    // read as a purpose-built OTP input instead of a generic 6-char text field.
    this.otpInput = el('input');
    this.otpInput.type = 'hidden';
    this.otpInput.addEventListener('input', () => {
      this.otpVerifyBtn.disabled = this.otpInput.value.trim().length !== 6;
    });
    this.otpInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.otpVerifyBtn.disabled) this.handleOtpVerify(kind, value);
    });
    field.appendChild(this.otpInput);

    const boxesWrap = el('div', 'pg-otp-boxes');
    const boxes: HTMLInputElement[] = [];
    const syncValue = () => {
      this.otpInput.value = boxes.map((b) => b.value).join('');
      this.otpInput.dispatchEvent(new Event('input'));
    };
    for (let i = 0; i < 6; i++) {
      const box = el('input', 'pg-otp-box');
      box.type = 'text';
      box.inputMode = 'numeric';
      box.maxLength = 1;
      box.autocomplete = i === 0 ? 'one-time-code' : 'off';
      box.addEventListener('input', () => {
        box.value = box.value.replace(/[^0-9]/g, '').slice(-1);
        if (box.value && i < 5) boxes[i + 1]!.focus();
        syncValue();
      });
      box.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !box.value && i > 0) boxes[i - 1]!.focus();
        if (e.key === 'Enter' && !this.otpVerifyBtn.disabled) this.handleOtpVerify(kind, value);
      });
      box.addEventListener('paste', (e) => {
        e.preventDefault();
        const text = (e.clipboardData?.getData('text') ?? '').replace(/[^0-9]/g, '').slice(0, 6);
        text.split('').forEach((ch, idx) => {
          if (boxes[idx]) boxes[idx]!.value = ch;
        });
        syncValue();
        boxes[Math.min(text.length, 5)]?.focus();
      });
      boxes.push(box);
      boxesWrap.appendChild(box);
    }
    field.appendChild(boxesWrap);

    const actionsRow = el('div', 'pg-otp-actions');
    const backBtn = el('button', 'pg-otp-link');
    backBtn.type = 'button';
    backBtn.textContent = kind === 'phone' ? '‹ Use a different phone number' : '‹ Use a different email address';
    backBtn.addEventListener('click', () => this.showForm());
    const resendBtn = el('button', 'pg-otp-link');
    resendBtn.type = 'button';
    resendBtn.textContent = 'Resend code';
    resendBtn.addEventListener('click', () => this.beginVerification(kind, value));
    actionsRow.appendChild(backBtn);
    actionsRow.appendChild(resendBtn);

    this.otpVerifyBtn = el('button', 'pg-submit-btn');
    this.otpVerifyBtn.type = 'button';
    this.otpVerifyBtn.textContent = 'Verify & book';
    this.otpVerifyBtn.disabled = true;
    this.otpVerifyBtn.style.background = this.branding?.primaryColor ?? '#2563eb';
    this.otpVerifyBtn.addEventListener('click', () => this.handleOtpVerify(kind, value));

    this.errorEl.style.display = 'none';
    this.mount.replaceChildren(field, this.errorEl, actionsRow, this.otpVerifyBtn);
    boxes[0]!.focus();
  }

  private async handleOtpVerify(kind: 'phone' | 'email', value: string) {
    const code = this.otpInput.value.trim();
    this.errorEl.style.display = 'none';
    this.otpVerifyBtn.disabled = true;
    this.otpVerifyBtn.textContent = 'Verifying…';
    try {
      const fullName = this.nameInput.value.trim() || undefined;
      const result = kind === 'phone' ? await this.api.verifyOtp(value, code, fullName) : await this.api.verifyEmailOtp(value, code, fullName);
      this.patientAccessToken = result.accessToken;
      if (kind === 'phone') this.verifiedPhone = value;
      else this.verifiedEmail = value;
    } catch (err) {
      this.otpVerifyBtn.disabled = false;
      this.otpVerifyBtn.textContent = 'Verify & book';
      this.showError(err instanceof Error ? err.message : "That code didn't look right — please try again.");
      return;
    }
    this.showForm();
    await this.performBooking();
  }

  private async performBooking() {
    // Omit phone/email entirely when blank rather than sending an empty
    // string — createPatientSchema's phone-or-email fields are `.optional()`
    // (undefined), not "present but empty"; an empty string fails their own
    // min-length/email format checks instead of being treated as absent.
    const phone = this.phoneInput.value.trim();
    const email = this.emailInput.value.trim();
    const verificationRequired = this.branding?.requiresPhoneVerificationForBooking ?? true;
    if (!this.selectedSlot && !this.selectedWindow) return;
    // An access token is required whenever verification is turned on and
    // some contact method was actually given — phone or email, whichever it
    // is, has already been through beginVerification/handleOtpVerify by the
    // time this runs (see handleSubmit).
    if ((phone || email) && verificationRequired && !this.patientAccessToken) return;
    this.errorEl.style.display = 'none';
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Booking…';
    const patient = {
      fullName: this.nameInput.value.trim(),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
    };
    const reasonForVisit = this.reasonInput.value.trim() || undefined;
    try {
      if (this.selectedWindow) {
        const result = await this.api.bookWindow(
          {
            doctorId: this.selectedWindow.doctorId,
            patient,
            windowStart: this.selectedWindow.startTimeIso,
            windowEnd: this.selectedWindow.endTimeIso,
            ...(reasonForVisit ? { reasonForVisit } : {}),
          },
          this.patientAccessToken ?? undefined,
        );
        this.showWindowSuccess(result);
      } else if (this.selectedSlot) {
        const result = await this.api.bookAppointment(
          {
            doctorId: this.selectedSlot.doctorId,
            patient,
            startTime: this.selectedSlot.startTimeIso,
            ...(reasonForVisit ? { reasonForVisit } : {}),
          },
          this.patientAccessToken ?? undefined,
        );
        this.showSuccess(result);
      }
    } catch (err) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Confirm booking';
      this.showError(err instanceof Error ? err.message : 'Something went wrong booking that appointment.');
      await this.refreshAvailability();
    }
  }

  /** Shared by both success screens — modal gets a "Done" button that closes
   * it (opts.onDone); an inline embed has nothing to close, so it offers
   * "Book another appointment" and resets the form fresh instead. */
  private successFooterButton(): HTMLButtonElement {
    const btn = el('button', 'pg-submit-btn');
    btn.type = 'button';
    btn.style.background = this.branding?.primaryColor ?? '#2563eb';
    if (this.onDone) {
      btn.textContent = 'Done';
      btn.addEventListener('click', () => this.onDone!());
    } else {
      btn.textContent = 'Book another appointment';
      btn.addEventListener('click', () => {
        this.patientAccessToken = null;
        this.verifiedPhone = null;
        this.render();
      });
    }
    return btn;
  }

  private showSuccess(result: BookAppointmentResponse) {
    this.mount.innerHTML = '';
    const wrap = el('div', 'pg-form-success');
    const icon = el('div', 'pg-form-success-icon');
    icon.innerHTML = SUCCESS_CHECK_SVG;
    const title = el('div', 'pg-form-success-title');
    title.textContent = result.status === 'CONFIRMED' ? "You're booked!" : 'Request sent!';
    const detail = el('div', 'pg-form-success-detail');
    detail.textContent =
      `${result.doctorName} — ${stripTimeZoneAbbrev(result.label)}` +
      (result.status === 'CONFIRMED' ? '' : ' (pending clinic confirmation)');
    wrap.appendChild(icon);
    wrap.appendChild(title);
    wrap.appendChild(detail);
    if (result.tokenNumber != null) {
      const token = el('div', 'pg-form-success-token');
      token.style.color = this.branding?.primaryColor ?? '#2563eb';
      token.textContent = `Your token number: #${result.tokenNumber}`;
      wrap.appendChild(token);
    }
    wrap.appendChild(this.successFooterButton());
    this.mount.appendChild(wrap);
  }

  /** Window/token-booking equivalent — leads with the token number and
   * presents the time as an approximation, never a promised clock time. */
  private showWindowSuccess(result: BookWindowResponse) {
    this.mount.innerHTML = '';
    const wrap = el('div', 'pg-form-success');
    const icon = el('div', 'pg-form-success-icon');
    icon.innerHTML = SUCCESS_CHECK_SVG;
    const title = el('div', 'pg-form-success-title');
    title.textContent = result.tokenNumber != null ? `You're booked! Token #${result.tokenNumber}` : "You're booked!";
    const detail = el('div', 'pg-form-success-detail');
    detail.textContent = `${result.doctorName} — estimated around ${stripTimeZoneAbbrev(result.estimatedTimeLabel)} (exact time may vary)`;
    wrap.appendChild(icon);
    wrap.appendChild(title);
    wrap.appendChild(detail);
    wrap.appendChild(this.successFooterButton());
    this.mount.appendChild(wrap);
  }
}
